import { z } from 'zod';
/**
 * A significant life event: something that happened, or a stretch of time that
 * something was true of.
 *
 * Stored as an ordinary row in `entries`, which already carries everything
 * this needs — `recorded_at` is when it started, `note` is what the owner
 * wrote about it, and the JSON `data` column (there since the table was
 * written, for exactly this) holds the title and, for a period, when it ended.
 * No new table, and so no migration.
 *
 * Its reason for existing beyond the record itself is context. A weight that
 * climbed five kilos means one thing on its own and another when a fortnight
 * away from home sits underneath it; a run of high blood pressure reads
 * differently over the month somebody's mother died. The app never draws that
 * conclusion — it puts the two on the same axis and lets the reader see what
 * they see.
 */
/** What goes in `entries.data` for an event. The rest lives in real columns. */
export const eventPayloadSchema = z.object({
    /** Short, and the only part shown on another node's chart. */
    title: z.string().trim().min(1).max(200),
    /**
     * When it stopped being true, for a period. Absent for a moment.
     *
     * A moment and a period are the same record with one field filled in or
     * not, rather than two shapes — "I was on holiday for a fortnight" and "my
     * mother died" are the same kind of thing to the person remembering them,
     * and the difference is only how they are drawn.
     */
    endsAt: z.string().datetime().optional(),
    /** What kind of thing this was. Free text; see `KNOWN_CATEGORIES`. */
    category: z.string().trim().min(1).max(40).optional(),
});
/**
 * The categories the app suggests, and the only ones it can draw a mark for.
 *
 * A suggestion list rather than an enum, because a life is not an enum: a
 * sabbatical, a renovation, a diagnosis nobody has a word for yet. Type
 * anything and it is kept as typed; type one of these and it also gets its
 * own icon.
 *
 * Kept here rather than in the web app so the form, the chart and anything
 * later that wants to filter are all working from one list.
 */
export const KNOWN_CATEGORIES = [
    'Holiday',
    'Loss',
    'Illness',
    'Move',
    'Work',
    'Relationship',
    'Milestone',
];
/**
 * Folds a category down to what two spellings of the same thing have in
 * common, for matching only — never for storing or showing.
 *
 * "Holiday", "holiday" and " HOLIDAY " are one category to a reader, and a
 * list that shows all three back is the failure this exists to avoid. What
 * the owner typed is what is stored and what is shown.
 */
export function normalizeCategory(category) {
    return category.trim().toLowerCase();
}
/** The suggested category this one matches, if it matches one at all. */
export function matchKnownCategory(category) {
    if (category === null)
        return null;
    const normalized = normalizeCategory(category);
    return KNOWN_CATEGORIES.find((known) => normalizeCategory(known) === normalized) ?? null;
}
/**
 * Every category already in use, in the order they were first used, with the
 * suggestions the owner has not used yet after them.
 *
 * What the form offers. Their own words come first because those are the ones
 * they will reach for again.
 */
export function categorySuggestions(events) {
    const seen = new Map();
    for (const event of events) {
        if (event.category === null)
            continue;
        const key = normalizeCategory(event.category);
        if (!seen.has(key))
            seen.set(key, event.category);
    }
    for (const known of KNOWN_CATEGORIES) {
        const key = normalizeCategory(known);
        if (!seen.has(key))
            seen.set(key, known);
    }
    return [...seen.values()];
}
/**
 * What a client sends to create or replace an event.
 *
 * Shared by the route that validates it and the form that builds it, so the
 * two cannot disagree about what a valid event is.
 */
export const eventInputSchema = z
    .object({
    /** When it happened, or when the period began. */
    startsAt: z.string().datetime(),
    title: z.string().trim().min(1).max(200),
    /** Present makes it a period; absent makes it a moment. */
    endsAt: z.string().datetime().optional(),
    note: z.string().trim().max(2000).optional(),
    category: z.string().trim().min(1).max(40).optional(),
})
    .refine((input) => input.endsAt === undefined || Date.parse(input.endsAt) >= Date.parse(input.startsAt), {
    message: 'endsAt must not be before startsAt',
    path: ['endsAt'],
});
/** One event, as every layer above the database passes it around. */
export const lifeEventSchema = z.object({
    id: z.string(),
    /** When it happened, or when the period began. From `entries.recorded_at`. */
    startsAt: z.string().datetime(),
    /** When the period ended. Null for a moment. */
    endsAt: z.string().datetime().nullable(),
    title: z.string(),
    /** The owner's own words. From `entries.note`. */
    note: z.string().nullable(),
    /** What kind of thing this was, as the owner typed it. Null when unsaid. */
    category: z.string().nullable(),
});
/** True when this event covers a stretch of time rather than an instant. */
export function isPeriod(event) {
    return event.endsAt !== null && event.endsAt !== event.startsAt;
}
/**
 * The events that have any part of themselves inside a window, for laying
 * over a chart that spans it.
 *
 * Overlap, not containment: a holiday that started before the chart's first
 * reading and ended inside it is exactly the context the reader is looking
 * for, and dropping it because it did not begin in frame would hide the half
 * that explains the climb.
 *
 * `from`/`to` are inclusive, and a moment sitting precisely on either edge
 * counts — the alternative is an event that vanishes at one zoom level and
 * reappears at the next.
 */
export function eventsInWindow(events, from, to) {
    const start = Date.parse(from);
    const end = Date.parse(to);
    if (Number.isNaN(start) || Number.isNaN(end))
        return [];
    return events.filter((event) => {
        const eventStart = Date.parse(event.startsAt);
        if (Number.isNaN(eventStart))
            return false;
        const eventEnd = event.endsAt === null ? eventStart : Date.parse(event.endsAt);
        // A period whose end is unparseable is still a real event; treat it as the
        // moment it began rather than discarding what the owner wrote.
        const resolvedEnd = Number.isNaN(eventEnd) ? eventStart : eventEnd;
        return eventStart <= end && resolvedEnd >= start;
    });
}
/**
 * Sorts events for reading: earliest first, and where two begin together the
 * longer one first, so a period is drawn before the moments that sit inside it
 * rather than over them.
 */
export function sortEvents(events) {
    return [...events].sort((a, b) => {
        const byStart = Date.parse(a.startsAt) - Date.parse(b.startsAt);
        if (byStart !== 0)
            return byStart;
        const aEnd = Date.parse(a.endsAt ?? a.startsAt);
        const bEnd = Date.parse(b.endsAt ?? b.startsAt);
        return bEnd - aEnd;
    });
}
//# sourceMappingURL=events.js.map